/**
 * AgentPortal's internal-SSO instance — the app's ONE auth setup point.
 *
 * The prebuilt @baltlife/internal-sso-core package can't read this app's build-time
 * env (its own import.meta.env is inlined at package build), so AgentPortal reads its
 * own env here and hands it over (explicit init). Everything auth-shaped hangs off
 * this instance: the route guard (sso.checkAuthenticated), the API adapters
 * (sso.getToken / sso.onUnauthorized), the callback exchange (sso.handleOAuthCallback),
 * logout, and proactive session renewal.
 *
 * React components reach it through <InternalSsoProvider sso={sso}> in App.jsx
 * (useSession resolves it from context).
 *
 * Env vars use the REACT_APP_* names the repo already reads (vite.config.js
 * envPrefix accepts both VITE_ and REACT_APP_). import.meta.env.DEV is a real
 * boolean under Vite.
 */
import { createInternalSso } from '@baltlife/internal-sso-core';

// Namespaces every key this app stores — session AND the package's sign-out latch, which
// is derived from this same prefix. They must stay in one namespace: a latch raised under
// a different prefix than the session it guards is never found by the route guard, which
// silently restores the redirect race the latch exists to prevent.
const STORAGE_KEY_PREFIX = 'agentportal.auth';

// The dev bypass short-circuits the route guard, so the app renders with NO Stytch
// session and therefore no token to decode claims from. AgentPortal has no mock-data
// layer standing in for those claims, and UserProvider blocks render until it has an
// npnID — so a bypassed session renders a permanently blank page that never redirects
// to login. It is therefore OPT-IN: unset means local dev runs the real SSO flow
// against the local auth site. Set REACT_APP_SSO_DEV_BYPASS=true to short-circuit.
export const devAuthBypass =
  import.meta.env.DEV && import.meta.env.REACT_APP_SSO_DEV_BYPASS === 'true';

// `?? ''` only narrows the value — a genuinely missing var still fails loud:
// createInternalSso throws ConfigError at bootstrap naming the empty option.
export const sso = createInternalSso({
  clientId: import.meta.env.REACT_APP_INTERNAL_SSO_CLIENT_ID ?? '',
  authSiteUrl: import.meta.env.REACT_APP_AUTH_SITE_URL ?? '',
  tokenEndpoint: import.meta.env.REACT_APP_INTERNAL_SSO_TOKEN_ENDPOINT ?? '',
  // Sign-out POSTs revocation straight to the backend rather than trusting the
  // auth-site redirect to reach it, so the package needs an absolute origin. Same
  // backend the API adapters already call, so it reuses that var rather than
  // introducing a second name for one origin.
  //
  // It must be a bare ORIGIN with no path suffix. The revocation path is concatenated
  // onto this value, and bootstrap validation only checks that each required option is
  // a non-empty string — never its shape. So an origin carrying `/api` is accepted and
  // then builds `/api/api/Auth/Logout`, which fails as a silent 404 rather than a
  // config error. Deployed envs substitute this var from a pipeline token, so that
  // shape is worth stating where the value is handed over.
  //
  // Stripping a trailing slash is belt-and-braces, not load-bearing: the package
  // normalizes that itself.
  apiBaseUrl: (import.meta.env.REACT_APP_EXTHUB_DOMAIN ?? '').replace(/\/+$/, ''),
  // false → real Stytch round-trip (guard redirects, real getToken).
  isDev: devAuthBypass,
  // App-specific storage keys — the package default is shared across apps and
  // would collide when more than one Connected-App consumer runs on the same origin.
  storageKeyPrefix: STORAGE_KEY_PREFIX,
});

// There is deliberately NO exported `isLoggingOut()` here, and nothing in this app may
// read the sign-out latch directly.
//
// The latch belongs to the package: `sso.logout()` raises it before clearing the store,
// and `sso.checkAuthenticated()` reads it. That read CONSUMES — a latch that has expired,
// or that was raised by a PRIOR document, is removed as it is reported, which is what
// stops the bounce-back from the auth site looping. So the latch answers exactly one
// caller, and any second reader takes the answer away from the first. When this app read
// it from its API adapters, they consumed it before the route guard ran; the guard then
// saw an ordinary sessionless load and sent the browser to authorize instead of logout.
//
// Anywhere this app would previously have asked "are we signing out?" it now asks
// `sso.checkAuthenticated()` for a DESTINATION instead. That one call distinguishes the
// two refusals by itself — logout url mid-sign-out, authorize url otherwise — so no
// caller has to know the latch exists.

// The one way to sign out. Callers must not reach for sso.logout() directly — this is the
// seam where app-level sign-out concerns land, and routing them through one function is
// what keeps that true. `sso.logout()` raises the latch, stands the proactive renewer down
// so no in-flight refresh can mint a replacement session, revokes at the backend, clears
// locally, and hands off to the auth site — in that order, on the click path.
export function signOut() {
  sso.logout();
}
